import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../layouts/DashboardLayout";
import { api } from "../../api/client";

const empty = {
  name: "",
  email: "",
  phone: "",
  password: "",
  venueName: "",
  city: "",
};

export default function AdminOwners() {
  const [owners, setOwners] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState("");

  async function load() {
    setOwners(await api("/api/admin/owners"));
  }

  useEffect(() => {
    load().catch(() => setOwners([]));
  }, []);

  async function createOwner(event) {
    event.preventDefault();
    setMessage("");
    try {
      await api("/api/admin/owners", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setForm(empty);
      setMessage("Account created. The owner can now dress their page.");
      await load();
    } catch (err) {
      setMessage(err.message);
    }
  }

  async function saveEdit(event) {
    event.preventDefault();
    try {
      await api(`/api/admin/owners/${editing.id}`, {
        method: "PUT",
        body: JSON.stringify(editing),
      });
      setEditing(null);
      await load();
    } catch (err) {
      setMessage(err.message);
    }
  }

  async function remove(id) {
    if (!window.confirm("Remove this owner, their venue page and bookings?")) return;
    await api(`/api/admin/owners/${id}`, { method: "DELETE" });
    await load();
  }

  return (
    <DashboardLayout title="Owner accounts">
      <div className="grid gap-8 xl:grid-cols-[0.9fr_1.1fr]">
        <form onSubmit={createOwner} className="card h-max p-6">
          <h2 className="font-serif text-2xl">Create a house</h2>
          <p className="mt-1 text-sm text-ink/55">
            New owners receive a private studio and a draft public page.
          </p>
          <div className="mt-5 space-y-3">
            <input className="field" placeholder="Owner name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="field" type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className="field" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <input className="field" type="password" placeholder="Password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <input className="field" placeholder="Venue name" value={form.venueName} onChange={(e) => setForm({ ...form, venueName: e.target.value })} />
            <input className="field" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          {message && <p className="mt-3 text-sm text-plum">{message}</p>}
          <button className="btn-rose mt-5 w-full">Create account</button>
        </form>

        <div className="card overflow-hidden">
          {owners.map((owner) => (
            <article key={owner.id} className="border-b border-ink/5 p-5 last:border-0">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-xl">{owner.name}</h3>
                  <p className="text-sm text-ink/55">{owner.email} · {owner.phone || "no phone"}</p>
                  <p className="mt-1 text-sm">
                    {owner.venue ? (
                      <>
                        {owner.venue.name} · {owner.venue.published ? "live" : "draft"}
                        {owner.venue.slug && (
                          <Link className="ml-2 text-rose" to={`/u/${owner.venue.slug}`}>
                            /u/{owner.venue.slug}
                          </Link>
                        )}
                      </>
                    ) : (
                      "No venue"
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button className="btn-ghost" onClick={() => setEditing({ ...owner, password: "" })}>
                    Edit
                  </button>
                  <button className="btn-ghost" onClick={() => remove(owner.id)}>
                    Remove
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
          <form onSubmit={saveEdit} className="card w-full max-w-md p-6">
            <h3 className="font-serif text-2xl">Edit {editing.name}</h3>
            <div className="mt-4 space-y-3">
              <input className="field" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <input className="field" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              <input className="field" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
              <select className="field" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                <option value="active">active</option>
                <option value="suspended">suspended</option>
              </select>
              <input className="field" type="password" placeholder="New password (optional)" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} />
            </div>
            <div className="mt-5 flex gap-3">
              <button className="btn-ink" type="submit">Save</button>
              <button className="btn-ghost" type="button" onClick={() => setEditing(null)}>
                Close
              </button>
            </div>
          </form>
        </div>
      )}
    </DashboardLayout>
  );
}
